import React, { useState } from "react";
import { useRedirect, useNotify, useTranslate, useGetList } from "react-admin";
import {
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  LinearProgress,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import UploadIcon from "@mui/icons-material/Upload";
import MicIcon from "@mui/icons-material/Mic";
import firebaseDataProvider from "../../providers/dataProvider";
import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { firebaseApp } from '../../providers/firebase';

const storage = getStorage(firebaseApp);

// Helper to extract YouTube video ID from various URL formats
const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/, // Direct video ID
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Convert YouTube live URLs to standard watch URLs
const normalizeYouTubeUrl = (url: string): string => {
  if (!url) return url;
  const liveMatch = url.match(/youtube\.com\/live\/([^&\n?#]+)/);
  if (liveMatch) {
    return `https://www.youtube.com/watch?v=${liveMatch[1]}`;
  }
  return url;
};

interface Episode {
  title: string;
  videoUrl: string;
  audioUrl: string;
  videoMode: 'url' | 'upload' | 'audio' | 'audioUrl';
  uploadedVideoUrl: string;
  uploadedAudioUrl: string;
  duration: number;
  description: string;
  uploading: boolean;
  uploadProgress: number;
}

interface CategoryOption {
  id: string;
  name: string;
}

const VideoShowCreate: React.FC = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const translate = useTranslate();
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showImage, setShowImage] = useState<any>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<CategoryOption[]>([]);
  const [actionType, setActionType] = useState("disabled");
  const [episodes, setEpisodes] = useState<Episode[]>([
    { title: "", videoUrl: "", audioUrl: "", videoMode: 'url', uploadedVideoUrl: "", uploadedAudioUrl: "", duration: 3600, description: "", uploading: false, uploadProgress: 0 },
  ]);

  // Fetch available categories
  const { data: categoriesData, isLoading: categoriesLoading } = useGetList('categories', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'priority', order: 'ASC' },
  });

  const categoryOptions: CategoryOption[] = (categoriesData || [])
    .filter((cat: any) => !cat.isPrimary || cat.layoutType === 'video' || cat.layoutType === 'grid')
    .map((cat: any) => ({ id: cat.id, name: cat.name }));

  const addEpisode = () => {
    setEpisodes([
      ...episodes,
      { title: "", videoUrl: "", audioUrl: "", videoMode: 'url', uploadedVideoUrl: "", uploadedAudioUrl: "", duration: 3600, description: "", uploading: false, uploadProgress: 0 },
    ]);
  };

  const removeEpisode = (index: number) => {
    setEpisodes(episodes.filter((_, i) => i !== index));
  };

  const updateEpisode = (index: number, field: keyof Episode, value: any) => {
    const newEpisodes = [...episodes];
    newEpisodes[index] = { ...newEpisodes[index], [field]: value };
    setEpisodes(newEpisodes);
  };


  // Helper to get video duration from a file
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration));
      };
      video.onerror = () => {
        resolve(3600); // Default to 1 hour if we can't get duration
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleVideoUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Only allow .mp4 video files
      const isMp4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
      if (!isMp4) {
        notify(translate('showsPage.invalidVideoFormat'), { type: "error" });
        e.target.value = '';
        return;
      }

      const newEpisodes = [...episodes];
      newEpisodes[index] = { ...newEpisodes[index], uploading: true, uploadProgress: 0 };
      setEpisodes(newEpisodes);

      try {
        // Get video duration before upload
        const duration = await getVideoDuration(file);

        const storagePath = `public/videos/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], uploadProgress: progress };
              return updated;
            });
          },
          (error) => {
            console.error("Error uploading video:", error);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], uploading: false, uploadProgress: 0 };
              return updated;
            });
            notify(translate('showsPage.uploadError'), { type: "error" });
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = {
                ...updated[index],
                uploadedVideoUrl: url,
                duration: duration,
                uploading: false,
                uploadProgress: 100,
              };
              return updated;
            });
            notify(translate('showsPage.videoUploadSuccess'), { type: "success" });
          }
        );
      } catch (error) {
        console.error("Error uploading video:", error);
        setEpisodes(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], uploading: false, uploadProgress: 0 };
          return updated;
        });
        notify("Error uploading video", { type: "error" });
      }
    }
  };

  // Helper to get audio duration from a file
  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audio.src);
        resolve(Math.round(audio.duration));
      };
      audio.onerror = () => {
        resolve(3600); // Default to 1 hour if we can't get duration
      };
      audio.src = URL.createObjectURL(file);
    });
  };

  const handleAudioUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const newEpisodes = [...episodes];
      newEpisodes[index] = { ...newEpisodes[index], uploading: true, uploadProgress: 0 };
      setEpisodes(newEpisodes);

      try {
        // Get audio duration before upload
        const duration = await getAudioDuration(file);

        const storagePath = `public/audio/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], uploadProgress: progress };
              return updated;
            });
          },
          (error) => {
            console.error("Error uploading audio:", error);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], uploading: false, uploadProgress: 0 };
              return updated;
            });
            notify(translate('showsPage.uploadError'), { type: "error" });
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setEpisodes(prev => {
              const updated = [...prev];
              updated[index] = {
                ...updated[index],
                uploadedAudioUrl: url,
                duration: duration,
                uploading: false,
                uploadProgress: 100,
              };
              return updated;
            });
            notify(translate('showsPage.audioUploadSuccess'), { type: "success" });
          }
        );
      } catch (error) {
        console.error("Error uploading audio:", error);
        setEpisodes(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], uploading: false, uploadProgress: 0 };
          return updated;
        });
        notify("Error uploading audio", { type: "error" });
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageUploading(true);
      const file = e.target.files[0];
      try {
        const storagePath = `categories/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);

        const asset = {
          type: 'image',
          createdAt: Date.now(),
          width: 0,
          height: 0,
          url,
        };
        setShowImage(asset);
        notify(translate('showsPage.imageUploadSuccess'), { type: "success" });
      } catch (error) {
        console.error("Error uploading image:", error);
        notify(translate('showsPage.uploadError'), { type: "error" });
      } finally {
        setImageUploading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      notify(translate('showsPage.validationTitleRequired'), { type: "warning" });
      return;
    }

    if (!showImage?.url) {
      notify(translate('showsPage.validationImageRequired'), { type: "warning" });
      return;
    }

    const episodeHasMedia = (ep: Episode) =>
      (ep.videoMode === 'url' && ep.videoUrl) ||
      (ep.videoMode === 'upload' && ep.uploadedVideoUrl) ||
      (ep.videoMode === 'audio' && ep.uploadedAudioUrl) ||
      (ep.videoMode === 'audioUrl' && ep.audioUrl);

    // Block saving while a media upload is still in progress
    if (episodes.some(ep => ep.uploading)) {
      notify(translate('showsPage.uploadInProgress'), { type: "warning" });
      return;
    }

    const hasValidMedia = episodes.some(episodeHasMedia);
    if (episodes.length === 0 || !hasValidMedia) {
      notify(translate('showsPage.validationEpisodeRequired'), {
        type: "warning",
      });
      return;
    }

    // Do not silently drop episodes the user filled in but that lack media
    const incompleteIndex = episodes.findIndex(ep =>
      (ep.title.trim() || ep.videoUrl || ep.audioUrl || ep.uploadedVideoUrl || ep.uploadedAudioUrl) &&
      !episodeHasMedia(ep)
    );
    if (incompleteIndex !== -1) {
      notify(translate('showsPage.episodeMissingMedia', { number: incompleteIndex + 1 }), { type: "warning" });
      return;
    }

    setSaving(true);

    try {
      const showId = `shcustom${Date.now()}${Math.random()
        .toString(36)
        .substring(2, 8)}`;

      // Filter episodes that have valid media before transforming
      const validEpisodes = episodes.filter(episodeHasMedia);

      // Transform episodes
      const transformedEpisodes = validEpisodes.map((ep, index) => {
        const isAudioEpisode = ep.videoMode === 'audio' || ep.videoMode === 'audioUrl';

        if (isAudioEpisode) {
          // Audio episode - has real sound, no video
          const audioUrl = ep.videoMode === 'audio' ? ep.uploadedAudioUrl : ep.audioUrl;
          return {
            id: `ep${showId}${index + 1}`,
            title: ep.title || `Episode ${index + 1}`,
            description: ep.description || "",
            season: new Date().getFullYear().toString(),
            episodeNumber: index + 1,
            duration: ep.duration || 3600,
            length: 144004079,
            sound: {
              url: audioUrl,
              duration: ep.duration || 3600,
              length: 144004079,
              type: "sound",
              createdAt: Date.now(),
            },
          };
        }

        // Video episode - has placeholder sound and real video
        const videoUrl = normalizeYouTubeUrl(ep.videoMode === 'upload' ? ep.uploadedVideoUrl : ep.videoUrl);
        const youtubeId = ep.videoMode === 'url' ? extractYouTubeId(videoUrl) : null;

        const video: any = {
          type: "video",
          createdAt: Date.now(),
          width: -1,
          height: -1,
          duration: ep.duration || 3600,
          url: videoUrl,
        };

        // Only add youtubeId if it exists (Firestore doesn't accept undefined)
        if (youtubeId) {
          video.youtubeId = youtubeId;
        }

        return {
          id: `ep${showId}${index + 1}`,
          title: ep.title || `Episode ${index + 1}`,
          description: ep.description || "",
          season: new Date().getFullYear().toString(),
          episodeNumber: index + 1,
          duration: ep.duration || 3600,
          length: 144004079,
          // @deprecated - sound field is no longer used for video episodes but kept for backward compatibility
          sound: {
            url: "https://google.com",
            duration: ep.duration || 3600,
            length: 144004079,
            type: "sound",
            createdAt: Date.now(),
          },
          video,
        };
      });

      // Use uploaded show image as assets (required)
      const showAssets = showImage?.url ? [showImage] : [];

      const showData = {
        id: showId,
        podtooId: "",
        username: "",
        title: title,
        titleLowercased: title.toLowerCase(),
        searchIndex: title.toLowerCase(),
        subTitle: subTitle,
        description: description,
        summary: "",
        about: "",
        categoryId: "",
        url: "",
        assets: showAssets,
        episodes: transformedEpisodes,
        episodesCount: transformedEpisodes.length,
        artists: [],
        artistIds: [],
        // Store selected category names for the sync service to place this show in those categories
        overrideCategories: selectedCategories.map(cat => cat.name),
        actionType,
      };

      // Save directly to sync/override/shows collection
      await firebaseDataProvider.create("sync/override/shows", {
        data: showData,
      });

      notify(translate('showsPage.createSuccess'), { type: "success" });
      redirect("/shows");
    } catch (error) {
      console.error("Error creating show:", error);
      notify(translate('showsPage.createError'), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mt: 2, maxWidth: 800, mx: "auto" }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {translate('showsPage.createShow')}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          {translate('showsPage.infoMessage')}
          <br />
          <strong>{translate('showsPage.infoNote')}</strong>
        </Alert>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={translate('showsPage.showTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label={translate('showsPage.subtitle')}
            value={subTitle}
            onChange={(e) => setSubTitle(e.target.value)}
            fullWidth
          />

          <TextField
            label={translate('showsPage.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <Autocomplete
            multiple
            options={categoryOptions}
            getOptionLabel={(option) => option.name}
            value={selectedCategories}
            onChange={(_, newValue) => setSelectedCategories(newValue)}
            loading={categoriesLoading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('showsPage.categories')}
                placeholder={translate('showsPage.selectCategories')}
                helperText={translate('showsPage.categoriesHelper')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />

          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              label="Action Type"
            >
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="chat">Chat</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
              Controls user interaction: Disabled = no action, Chat = live chat.
            </Typography>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              {translate('showsPage.showImage')} *
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="show-image-upload"
            />
            <label htmlFor="show-image-upload">
              <Button
                variant="contained"
                component="span"
                disabled={imageUploading}
                sx={{ width: 200 }}
              >
                {imageUploading ? translate('showsPage.uploading') : translate('showsPage.uploadImage')}
              </Button>
            </label>
            {showImage?.url && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={showImage.url}
                  alt="Show preview"
                  style={{
                    maxHeight: 200,
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            {translate('showsPage.episodes')}
          </Typography>

          {episodes.map((episode, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }} variant="outlined">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">
                  {translate('showsPage.episode')} {index + 1}
                </Typography>
                {episodes.length > 1 && (
                  <IconButton
                    onClick={() => removeEpisode(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label={translate('showsPage.episodeTitle')}
                  value={episode.title}
                  onChange={(e) => updateEpisode(index, "title", e.target.value)}
                  required
                  fullWidth
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {translate('showsPage.mediaSource')}
                  </Typography>
                  <ToggleButtonGroup
                    value={episode.videoMode}
                    exclusive
                    onChange={(_, newMode) => {
                      if (newMode !== null) {
                        updateEpisode(index, "videoMode", newMode);
                      }
                    }}
                    size="small"
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="url">
                      <LinkIcon sx={{ mr: 1 }} />
                      {translate('showsPage.videoUrl')}
                    </ToggleButton>
                    <ToggleButton value="upload">
                      <UploadIcon sx={{ mr: 1 }} />
                      {translate('showsPage.videoUpload')}
                    </ToggleButton>
                    <ToggleButton value="audioUrl">
                      <LinkIcon sx={{ mr: 1 }} />
                      {translate('showsPage.audioUrl')}
                    </ToggleButton>
                    <ToggleButton value="audio">
                      <MicIcon sx={{ mr: 1 }} />
                      {translate('showsPage.audioUpload')}
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {episode.videoMode === 'url' && (
                    <Box>
                      <TextField
                        label={translate('showsPage.videoUrl')}
                        value={episode.videoUrl}
                        onChange={(e) => updateEpisode(index, "videoUrl", e.target.value)}
                        required
                        fullWidth
                        helperText={translate('showsPage.videoUrlHelper')}
                      />
                    </Box>
                  )}

                  {episode.videoMode === 'upload' && (
                    <Box>
                      <input
                        type="file"
                        accept="video/mp4,.mp4"
                        onChange={(e) => handleVideoUpload(index, e)}
                        style={{ display: "none" }}
                        id={`video-upload-${index}`}
                      />
                      <label htmlFor={`video-upload-${index}`}>
                        <Button
                          variant="contained"
                          component="span"
                          disabled={episode.uploading}
                          startIcon={episode.uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                          sx={{ width: 200 }}
                        >
                          {episode.uploading ? `${translate('showsPage.uploading')} ${episode.uploadProgress}%` : translate('showsPage.uploadVideo')}
                        </Button>
                      </label>
                      {episode.uploading && (
                        <Box sx={{ mt: 1, width: '100%' }}>
                          <LinearProgress variant="determinate" value={episode.uploadProgress} />
                        </Box>
                      )}
                      {episode.uploadedVideoUrl && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="success.main">
                            {translate('showsPage.videoUploadSuccess')}
                          </Typography>
                          <video
                            src={episode.uploadedVideoUrl}
                            controls
                            style={{
                              marginTop: 10,
                              maxHeight: 200,
                              maxWidth: "100%",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {episode.videoMode === 'audioUrl' && (
                    <Box>
                      <TextField
                        label={translate('showsPage.audioUrl')}
                        value={episode.audioUrl}
                        onChange={(e) => updateEpisode(index, "audioUrl", e.target.value)}
                        required
                        fullWidth
                        helperText={translate('showsPage.audioUrlHelper')}
                      />
                      {episode.audioUrl && (
                        <Box sx={{ mt: 2 }}>
                          <audio
                            src={episode.audioUrl}
                            controls
                            style={{ marginTop: 10, maxWidth: "100%" }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}

                  {episode.videoMode === 'audio' && (
                    <Box>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => handleAudioUpload(index, e)}
                        style={{ display: "none" }}
                        id={`audio-upload-${index}`}
                      />
                      <label htmlFor={`audio-upload-${index}`}>
                        <Button
                          variant="contained"
                          component="span"
                          disabled={episode.uploading}
                          startIcon={episode.uploading ? <CircularProgress size={16} /> : <MicIcon />}
                          sx={{ width: 200 }}
                        >
                          {episode.uploading ? `${translate('showsPage.uploading')} ${episode.uploadProgress}%` : translate('showsPage.uploadAudio')}
                        </Button>
                      </label>
                      {episode.uploading && (
                        <Box sx={{ mt: 1, width: '100%' }}>
                          <LinearProgress variant="determinate" value={episode.uploadProgress} />
                        </Box>
                      )}
                      {episode.uploadedAudioUrl && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="success.main">
                            {translate('showsPage.audioUploadSuccess')}
                          </Typography>
                          <audio
                            src={episode.uploadedAudioUrl}
                            controls
                            style={{
                              marginTop: 10,
                              maxWidth: "100%",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <TextField
                  label={translate('resources.shows.fields.duration')}
                  type="number"
                  value={episode.duration}
                  onChange={(e) =>
                    updateEpisode(index, "duration", parseInt(e.target.value) || 3600)
                  }
                  helperText={
                    (episode.videoMode === 'upload' || episode.videoMode === 'audio')
                      ? translate('showsPage.durationHelper')
                      : ""
                  }
                  sx={{ width: 200 }}
                />

                <TextField
                  label={translate('showsPage.episodeDescription')}
                  value={episode.description}
                  onChange={(e) =>
                    updateEpisode(index, "description", e.target.value)
                  }
                  multiline
                  rows={2}
                  fullWidth
                />
              </Box>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addEpisode}
            sx={{ alignSelf: "flex-start" }}
          >
            {translate('showsPage.addEpisode')}
          </Button>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={saving || episodes.some(ep => ep.uploading)}
            >
              {saving ? translate('showsPage.creating') : translate('showsPage.createShow')}
            </Button>
            <Button variant="outlined" onClick={() => redirect("/shows")}>
              {translate('showsPage.cancel')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VideoShowCreate;
